import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Input,
  List,
  message,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Tag,
  theme,
  Typography,
} from "antd";
import {
  CheckCircleTwoTone,
  EditOutlined,
  MailOutlined,
  TranslationOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useAppDispatch } from "../../../../app/hooks";
import {
  IReleaseNote,
  IReleaseNoteContent,
  IReleaseNotePublishBlocker,
  ReleaseNoteAudience,
  ReleaseNoteLanguage,
  ReleaseNotePublishStatus,
} from "../../../../interfaces/releaseNote.interface";
import {
  createReleaseNoteThunk,
  getReleaseNoteThunk,
  translateReleaseNoteThunk,
  updateReleaseNoteThunk,
} from "../../releaseNotesSlice";
import {
  AUDIENCE_LABELS,
  AUDIENCE_ORDER,
  findPublishBlockers,
  LANGUAGE_LABELS,
  LANGUAGE_ORDER,
} from "../../releaseNotesHelpers";
import EmailPreviewModal from "../EmailPreviewModal/EmailPreviewModal";
import MarkdownEditor from "../MarkdownEditor/MarkdownEditor";
import PublishModal from "../PublishModal/PublishModal";

const { Text } = Typography;

type ContentDraft = { title: string; body: string };
type ContentMap = Record<string, ContentDraft>;

const contentKey = (
  audience: ReleaseNoteAudience,
  language: ReleaseNoteLanguage
) => `${audience}:${language}`;

interface ReleaseNoteEditorProps {
  open: boolean;
  releaseNoteId: string | null;
  onClose: () => void;
  onPublished?: (note: IReleaseNote) => void;
}

const ReleaseNoteEditor: React.FC<ReleaseNoteEditorProps> = ({
  open,
  releaseNoteId,
  onClose,
  onPublished,
}) => {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();

  const [note, setNote] = useState<IReleaseNote | null>(null);
  const [releaseDate, setReleaseDate] = useState<Dayjs | null>(dayjs());
  const [versions, setVersions] = useState<string[]>([]);
  const [contentMap, setContentMap] = useState<ContentMap>({});
  const [audience, setAudience] = useState<ReleaseNoteAudience>(
    ReleaseNoteAudience.Merchants
  );
  const [language, setLanguage] = useState<ReleaseNoteLanguage>(
    ReleaseNoteLanguage.English
  );
  const [saving, setSaving] = useState(false);
  const [imagesUploading, setImagesUploading] = useState(false);
  const [publishBlockers, setPublishBlockers] = useState<
    IReleaseNotePublishBlocker[]
  >([]);
  const [translating, setTranslating] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const hydrate = useCallback((loaded: IReleaseNote) => {
    setNote(loaded);
    setReleaseDate(loaded.releaseDate ? dayjs(loaded.releaseDate) : null);
    setVersions(
      (loaded.versions ?? []).map((v) =>
        typeof v === "string" ? v : (v as { version: string }).version
      )
    );
    const map: ContentMap = {};
    loaded.content.forEach((c) => {
      map[contentKey(c.audience, c.language)] = {
        title: c.title,
        body: c.body,
      };
    });
    setContentMap(map);
  }, []);

  useEffect(() => {
    if (!open) return;
    setAudience(ReleaseNoteAudience.Merchants);
    setLanguage(ReleaseNoteLanguage.English);
    if (releaseNoteId) {
      dispatch(getReleaseNoteThunk(releaseNoteId))
        .unwrap()
        .then(hydrate)
        .catch(() => message.error("Failed to load release note."));
    } else {
      setNote(null);
      setReleaseDate(dayjs());
      setVersions([]);
      setContentMap({});
    }
  }, [open, releaseNoteId, dispatch, hydrate]);

  const activeKey = contentKey(audience, language);
  const activeDraft = contentMap[activeKey] ?? { title: "", body: "" };

  const setDraft = (patch: Partial<ContentDraft>) => {
    setContentMap((prev) => ({
      ...prev,
      [activeKey]: {
        ...(prev[activeKey] ?? { title: "", body: "" }),
        ...patch,
      },
    }));
  };

  const buildContent = useCallback((): IReleaseNoteContent[] => {
    return Object.entries(contentMap)
      .map(([key, draft]) => {
        const [aud, lang] = key.split(":") as [
          ReleaseNoteAudience,
          ReleaseNoteLanguage,
        ];
        return {
          audience: aud,
          language: lang,
          title: draft.title ?? "",
          body: draft.body ?? "",
        };
      })
      .filter((c) => c.title.trim() !== "" || c.body.trim() !== "");
  }, [contentMap]);

  const persist = useCallback(async (): Promise<IReleaseNote | null> => {
    if (!releaseDate) {
      message.warning("Please pick a release date.");
      return null;
    }
    const payload = {
      releaseDate: releaseDate.toISOString(),
      versions,
      content: buildContent(),
    };
    if (note?._id) {
      return dispatch(
        updateReleaseNoteThunk({ id: note._id, payload })
      ).unwrap();
    }
    return dispatch(createReleaseNoteThunk(payload)).unwrap();
  }, [releaseDate, versions, buildContent, note, dispatch]);

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const saved = await persist();
      if (saved) {
        hydrate(saved);
        message.success("Draft saved.");
      }
    } catch (err) {
      message.error("Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  const targetLanguages = useMemo(
    () => LANGUAGE_ORDER.filter((l) => l !== language),
    [language]
  );

  const handleTranslate = async () => {
    if (!activeDraft.title.trim()) {
      message.warning("Add a title for this language before translating.");
      return;
    }
    if (!activeDraft.body.trim()) {
      message.warning("Add content before translating.");
      return;
    }
    setTranslating(true);
    try {
      const saved = await persist();
      if (!saved) return;
      const translated = await dispatch(
        translateReleaseNoteThunk({
          id: saved._id,
          payload: {
            audience,
            sourceLanguage: language,
            targetLanguages,
          },
        })
      ).unwrap();
      hydrate(translated);
      message.success("Content translated.");
    } catch (err) {
      message.error("Failed to translate content.");
    } finally {
      setTranslating(false);
    }
  };

  const handleOpenPublish = async () => {
    const blockers = findPublishBlockers(buildContent());
    if (blockers.length > 0) {
      setPublishBlockers(blockers);
      return;
    }
    setSaving(true);
    try {
      const saved = await persist();
      if (saved) {
        hydrate(saved);
        setPublishOpen(true);
      }
    } catch (err) {
      message.error("Save before publishing failed.");
    } finally {
      setSaving(false);
    }
  };

  const audienceIsPublished = (target: ReleaseNoteAudience) =>
    note?.audienceStates.find((s) => s.audience === target)?.status ===
    ReleaseNotePublishStatus.Published;

  const audienceHasDraft = (target: ReleaseNoteAudience) =>
    LANGUAGE_ORDER.some((l) => {
      const draft = contentMap[contentKey(target, l)];
      return draft && (draft.title.trim() !== "" || draft.body.trim() !== "");
    });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="90%"
      title={releaseNoteId ? "Edit Release Note" : "New Release Note"}
      extra={
        <Space>
          <Button
            loading={saving}
            disabled={imagesUploading}
            title={imagesUploading ? "Images are still uploading…" : undefined}
            onClick={handleSaveDraft}
          >
            Save Draft
          </Button>
          <Button
            icon={<MailOutlined />}
            disabled={!activeDraft.title.trim() && !activeDraft.body.trim()}
            onClick={() => setPreviewOpen(true)}
          >
            Preview Email
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={imagesUploading || (!note && buildContent().length === 0)}
            title={imagesUploading ? "Images are still uploading…" : undefined}
            onClick={handleOpenPublish}
          >
            Publish All Ready Versions
          </Button>
        </Space>
      }
    >
      <Row gutter={24}>
        <Col xs={24} md={7} lg={6}>
          <Text type="secondary">TARGET AUDIENCE</Text>
          <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
            {AUDIENCE_ORDER.map((target) => {
              const selected = target === audience;
              return (
                <div
                  key={target}
                  onClick={() => setAudience(target)}
                  style={{
                    cursor: "pointer",
                    padding: `${token.paddingSM}px ${token.padding}px`,
                    borderRadius: token.borderRadius,
                    border: `1px solid ${
                      selected ? token.colorPrimary : token.colorBorderSecondary
                    }`,
                    background: selected
                      ? token.colorPrimaryBg
                      : token.colorBgContainer,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text strong={selected}>{AUDIENCE_LABELS[target]}</Text>
                  {audienceIsPublished(target) ? (
                    <Tag color="success" style={{ marginInlineEnd: 0 }}>
                      Published
                    </Tag>
                  ) : audienceHasDraft(target) ? (
                    <CheckCircleTwoTone twoToneColor={token.colorSuccess} />
                  ) : null}
                </div>
              );
            })}
          </Space>

          <div style={{ marginTop: token.marginLG }}>
            <Text type="secondary">LANGUAGE</Text>
            <div style={{ marginTop: 8 }}>
              <Segmented
                value={language}
                onChange={(val) => setLanguage(val as ReleaseNoteLanguage)}
                options={LANGUAGE_ORDER.map((l) => ({
                  label: LANGUAGE_LABELS[l],
                  value: l,
                }))}
              />
            </div>
          </div>

          <div style={{ marginTop: token.marginLG }}>
            <Space align="start">
              <EditOutlined />
              <Text type="secondary">
                Editing {AUDIENCE_LABELS[audience]} ({LANGUAGE_LABELS[language]}
                ) version.
              </Text>
            </Space>
          </div>
        </Col>

        <Col xs={24} md={17} lg={18}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Text type="secondary">Release Date</Text>
              <DatePicker
                style={{ width: "100%", marginTop: 8 }}
                value={releaseDate}
                onChange={setReleaseDate}
                format="MMM D, YYYY"
              />
            </Col>
            <Col xs={24} md={16}>
              <Text type="secondary">Released Versions</Text>
              <Select
                mode="tags"
                style={{ width: "100%", marginTop: 8 }}
                placeholder="Add version (e.g. Android v1.0.0) — Enter or ; to separate"
                value={versions}
                onChange={setVersions}
                tokenSeparators={[";", "\n"]}
                open={false}
                suffixIcon={null}
              />
            </Col>
          </Row>

          <div style={{ marginTop: token.marginLG }}>
            <Input
              size="large"
              placeholder="Release note title"
              value={activeDraft.title}
              onChange={(e) => setDraft({ title: e.target.value })}
              variant="borderless"
              style={{ paddingInline: 0, fontSize: 22, fontWeight: 600 }}
            />
          </div>

          <div style={{ marginTop: token.margin }}>
            <MarkdownEditor
              value={activeDraft.body}
              onChange={(body) => setDraft({ body })}
              onUploadingChange={setImagesUploading}
            />
          </div>

          <div style={{ marginTop: token.margin }}>
            <Button
              type="link"
              icon={<TranslationOutlined />}
              loading={translating}
              disabled={imagesUploading}
              onClick={handleTranslate}
              style={{ paddingInline: 0 }}
            >
              Auto-translate this content to{" "}
              {targetLanguages.map((l) => LANGUAGE_LABELS[l]).join(" & ")}
            </Button>
          </div>
        </Col>
      </Row>

      {note && publishOpen && (
        <PublishModal
          open={publishOpen}
          releaseNote={note}
          onClose={() => setPublishOpen(false)}
          onPublished={() => {
            setPublishOpen(false);
            const published = note;
            onClose();
            if (published) onPublished?.(published);
          }}
        />
      )}

      <EmailPreviewModal
        open={previewOpen}
        title={activeDraft.title}
        body={activeDraft.body}
        locale={language}
        onClose={() => setPreviewOpen(false)}
      />

      <Modal
        open={publishBlockers.length > 0}
        title="Finish these variants before publishing"
        onCancel={() => setPublishBlockers([])}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setPublishBlockers([])}
          >
            Got it
          </Button>,
        ]}
      >
        <Text type="secondary">
          Every started variant must have both a title and a body. These are
          incomplete:
        </Text>
        <List
          size="small"
          style={{ marginTop: token.margin }}
          dataSource={publishBlockers}
          renderItem={(blocker) => (
            <List.Item>
              <Space>
                <Tag color="blue">{AUDIENCE_LABELS[blocker.audience]}</Tag>
                <Tag>{LANGUAGE_LABELS[blocker.language]}</Tag>
                <Text type="danger">Missing {blocker.missing.join(" & ")}</Text>
              </Space>
            </List.Item>
          )}
        />
      </Modal>
    </Drawer>
  );
};

export default ReleaseNoteEditor;
